import { $t } from '@/i18n';
import { defineComponent, onMounted, onUnmounted, ref } from '@vue/composition-api';
import { jsonToYaml, yamlToJson } from '@/utils/merge/jsonToYaml';
import { useRouter } from '@/router/useRouter';
import { aiMergeEditorRoutePath } from '@/router/route-const';
import { BASE_PATH } from '@/config/constant';
import { BuildingBlocksAnimation } from '@/pages/project/project-generator/loading';
const AI_MERGE_HOST_SOURCE = 'ai-editor-merge-host';
const AI_MERGE_IFRAME_SOURCE = 'ai-editor-merge';
const SAVE_TIMEOUT_MS = 120000;
/**
 * AI 冲突编辑器：deferSave 下 gogo 先 preview，父页提交前 requestSave 真落盘。
 */
export default defineComponent({
    name: 'AIConflictEditorView',
    props: {
        conflictItem: {
            type: Object,
            default: () => ({}),
        },
        currentBranchLabel: {
            type: String,
            default: '',
        },
        mergeBranchLabel: {
            type: String,
            default: '',
        },
        sourceBranchId: {
            type: String,
            default: '',
        },
        targetBranchId: {
            type: String,
            default: '',
        },
    },
    emits: ['change'],
    setup(props, { emit }) {
        const { route } = useRouter();
        const iframeRef = ref(null);
        const showLoading = ref(true);
        let pendingSave = null;
        const buildIframeSrc = () => {
            const projectId = route.value?.query?.projectid || '';
            const branch = route.value?.query?.branch || '';
            let ours = props.conflictItem?.oursData?.mainBranch || '';
            const theirs = props.conflictItem?.theirsData?.mainBranch || '';
            if (!ours && props.conflictItem?.before) {
                try {
                    ours = yamlToJson(props.conflictItem.before).mainBranch || '';
                }
                catch {
                    // ignore
                }
            }
            const params = new URLSearchParams({
                projectid: projectId,
                branch,
                ours,
                theirs,
                oursName: props.currentBranchLabel,
                theirsName: props.mergeBranchLabel,
                deferSave: '1',
            });
            const basePath = BASE_PATH || '';
            return `${basePath.replace(/\/$/, '')}${aiMergeEditorRoutePath}?${params.toString()}`;
        };
        const iframeSrc = ref(buildIframeSrc());
        const clearPendingSave = () => {
            if (!pendingSave)
                return;
            clearTimeout(pendingSave.timer);
            pendingSave = null;
        };
        const finishPendingSave = (result) => {
            if (!pendingSave)
                return;
            const { resolve, reject } = pendingSave;
            clearPendingSave();
            result.ok ? resolve(result.value) : reject(result.error);
        };
        const emitChange = (data, resolved) => {
            emit('change', { data, resolved, awaitingProjectInfo: false });
        };
        const requestSave = () => new Promise((resolve, reject) => {
            const iframe = iframeRef.value;
            if (!iframe?.contentWindow) {
                reject(new Error($t('k_f10de695')));
                return;
            }
            clearPendingSave();
            pendingSave = {
                resolve,
                reject,
                timer: setTimeout(() => {
                    pendingSave = null;
                    reject(new Error($t('k_191b4dad')));
                }, SAVE_TIMEOUT_MS),
            };
            iframe.contentWindow.postMessage({
                source: AI_MERGE_HOST_SOURCE,
                type: 'requestSave',
                payload: {
                    sourceBranchId: props.sourceBranchId || undefined,
                    targetBranchId: props.targetBranchId || undefined,
                },
            }, '*');
        });
        const handleMessage = (event) => {
            const { data } = event;
            if (data?.source !== AI_MERGE_IFRAME_SOURCE)
                return;
            switch (data.type) {
                case 'conflictResolved': {
                    const preview = data.payload?.previewProjectInfo;
                    emitChange(preview ? jsonToYaml(preview) : '', Boolean(preview && data.payload?.success !== false));
                    break;
                }
                case 'mergeSuccess': {
                    const projectInfo = data.payload?.projectInfo;
                    emitChange(projectInfo ? jsonToYaml(projectInfo) : '', true);
                    if (projectInfo) {
                        finishPendingSave({ ok: true, value: projectInfo });
                    }
                    else {
                        finishPendingSave({
                            ok: false,
                            error: new Error($t('k_3781cdf2')),
                        });
                    }
                    break;
                }
                case 'error':
                    finishPendingSave({
                        ok: false,
                        error: new Error(data.payload?.message || $t('k_6e133be7')),
                    });
                    break;
                default:
                    break;
            }
            showLoading.value = false;
        };
        onMounted(() => {
            window.addEventListener('message', handleMessage);
        });
        onUnmounted(() => {
            window.removeEventListener('message', handleMessage);
            clearPendingSave();
        });
        return {
            iframeRef,
            iframeSrc,
            showLoading,
            requestSave,
        };
    },
    render() {
        return (<div class="relative h-full min-h-0 w-full">
        <iframe ref="iframeRef" src={this.iframeSrc} class="h-full w-full border-0" sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-clipboard-read allow-clipboard-write"/>
        {this.showLoading && (<BuildingBlocksAnimation fullscreen text={$t('k_dce404e9')}/>)}
      </div>);
    },
});
